import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";
import { BackToLoginButton } from "@/features/auth/components/LoginForm/Forms/FormParts/BackToLoginButton";
import { LoginFormError } from "@/features/auth/components/LoginForm/Forms/FormParts/LoginFormError";
import { SecurityStepHeader } from "@/features/auth/components/LoginForm/Forms/FormParts/SecurityStepHeader";
import TwoFaNotice from "@/features/auth/components/LoginForm/Forms/FormParts/TwoFaNotice/TwoFaNotice";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { type TwoFactorFormData, twoFactorFormSchema } from "@/features/auth/schemas/forms/twoFactorForm.schema";
import { APP_APPROVAL_CHANNEL } from "@/features/auth/types/auth.type";
import { ONBOARDING_COPY } from "@/features/legal/config/onboarding.config";
import { ButtonWithLoader } from "@/shared/components/customButtons/ButtonWithSpinner";
import { ControlledOTPInput } from "@/shared/components/forms/ControlledOTPInput";

const TWO_FA_COPY = {
  TITLE: "C'est bien vous ?",
  DESCRIPTION: "Saisissez le code de vérification reçu sur votre téléphone pour sécuriser votre connexion.",
  LOST_PHONE: "Vous n'avez plus accès à ce numéro ? Contactez",
  SUPPORT_EMAIL: "tickets@yeati.co",
} as const;

type TwoFAFormProps = {
  loginData: {
    email: string
    password: string
  }
  onBackToLogin: () => void
};

export const TwoFAForm = ({ loginData, onBackToLogin }: TwoFAFormProps) => {
  const {
    isTwoFactorRequired,
    isAuthenticating,
    error,
    clearError,
    check2FA,
    twoFactorData: { timestamp, channel },
  } = useAuth();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = useForm<TwoFactorFormData>({
    resolver: zodResolver(twoFactorFormSchema),
    defaultValues: { otp_code: "" },
    mode: "onSubmit",
  });

  const otpCode = watch("otp_code");

  const onSubmitForm = async (formData: TwoFactorFormData) => {
    clearError();
    await check2FA({
      username: loginData.email,
      password: loginData.password,
      otp_code: formData.otp_code,
    });
  };

  useEffect(() => {
    if (otpCode.length === 6) {
      handleSubmit(onSubmitForm)();
    }
  }, [otpCode]);

  // A sign-in approved in the app has its own screen (AppApprovalForm).
  if (!isTwoFactorRequired || channel === APP_APPROVAL_CHANNEL) return null;

  return (
    <div className="space-y-6">
      <SecurityStepHeader title={TWO_FA_COPY.TITLE} description={TWO_FA_COPY.DESCRIPTION} />
      <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6 overflow-visible">
        <LoginFormError title={error.title} description={error.description} />

        <ControlledOTPInput
          id="otp-input"
          label="Code de vérification à 6 chiffres"
          name="otp_code"
          control={control}
          autoFocus
          isRequired
        />

        <div className="flex flex-wrap items-center justify-end gap-4">
          <ButtonWithLoader
            type="submit"
            data-testid="otp-submit-button"
            isLoading={isAuthenticating}
            disabled={isSubmitting}
            color="primary"
            pill
          >
            <span className="flex items-center gap-2">
              {isAuthenticating ? "Vérification en cours..." : "Vérifier mon code"}
              <ArrowRight size={16} aria-hidden />
            </span>
          </ButtonWithLoader>
        </div>
        {timestamp && <TwoFaNotice timestamp={timestamp} />}
      </form>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        {TWO_FA_COPY.LOST_PHONE}
        {" "}
        <a href={`mailto:${TWO_FA_COPY.SUPPORT_EMAIL}`} className="text-primary underline">{TWO_FA_COPY.SUPPORT_EMAIL}</a>
        .
      </p>
      <BackToLoginButton onClick={onBackToLogin} />
      <p className="text-xs text-gray-500 dark:text-gray-400">{ONBOARDING_COPY.SECURITY_FOOTNOTE}</p>
    </div>
  );
};
